import { useContext, useReducer, createContext, useState, useCallback } from 'react';
import isEqual from 'react-fast-compare';
import { getUnixTime } from 'date-fns';

import { useQueryParams } from '/src/hooks';
import { getParam } from '/src/utils/location';
import { getInitialDate } from '/src/utils/date';
import { SPECIAL_DATE_RANGES } from '../_global/constants';

const ACTIONS = {
  SET: 'SET',
};
const { SET } = ACTIONS;

const FunnelEditorContext = createContext();

const initialState = (funnel) => ({
  funnel,
  date: getInitialDate(getParam('date')) ?? { special: SPECIAL_DATE_RANGES.LAST_30_DAYS },
});

export function FunnelEditorProvider({ funnel, children }) {
  function funnelReducer(state, action) {
    switch (action.type) {
      case SET:
        return {
          ...state,
          ...action.payload,
        };
      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(funnelReducer, initialState(funnel));
  const [funnelState, setFunnelState] = useState({ state, dispatch });
  if (!isEqual(state, funnelState.state)) {
    setFunnelState({ state, dispatch });
  }
  return <FunnelEditorContext.Provider value={funnelState}>{children}</FunnelEditorContext.Provider>;
}

export function useFunnelEditorContext() {
  const { set: queryParamsSet } = useQueryParams();
  const { state, dispatch } = useContext(FunnelEditorContext);

  const setDateRange = useCallback(
    (value) => {
      if (value.start_date) {
        queryParamsSet(
          'date',
          JSON.stringify({
            start_date: getUnixTime(value.start_date),
            end_date: getUnixTime(value.end_date),
          }),
        );
      } else {
        queryParamsSet('date', JSON.stringify(value));
      }
      dispatch({ type: SET, payload: { date: value } });
    },
    [queryParamsSet, dispatch],
  );

  const setFunnel = useCallback((funnel) => dispatch({ type: SET, payload: { funnel } }), [dispatch]);

  const activateAddStep = useCallback(
    () => dispatch({ type: SET, payload: { addStepActive: true, stepPickerActiveIndex: null } }),
    [dispatch],
  );

  const activateChangeStep = useCallback(
    (index) => dispatch({ type: SET, payload: { addStepActive: false, stepPickerActiveIndex: index } }),
    [dispatch],
  );

  const deactiveActiveSteps = useCallback(
    () => dispatch({ type: SET, payload: { addStepActive: false, stepPickerActiveIndex: null } }),
    [dispatch],
  );

  return {
    ...state,
    setDateRange,
    setFunnel,
    activateAddStep,
    activateChangeStep,
    deactiveActiveSteps,
  };
}
